import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CANCELLATION_REASONS = [
  { id: 'too_expensive', label: 'Too expensive' },
  { id: 'not_using', label: 'Not using it enough' },
  { id: 'found_alternative', label: 'Found a better alternative' },
  { id: 'missing_features', label: 'Missing features I need' },
  { id: 'technical_issues', label: 'Technical issues' },
  { id: 'other', label: 'Other' }
];

export default function FeedbackPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [additionalFeedback, setAdditionalFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }

    // Validate token
    axios.get(`${API}/feedback/validate/${token}`)
      .then(() => {
        setValid(true);
        setValidating(false);
      })
      .catch(() => {
        setValid(false);
        setValidating(false);
      });
  }, [token]);

  const handleReasonToggle = (reasonId) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(id => id !== reasonId)
        : [...prev, reasonId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedReasons.length === 0 && !additionalFeedback.trim()) {
      toast.error('Please select at least one reason or provide feedback');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback/submit`, {
        token,
        reasons: selectedReasons,
        additional_feedback: additionalFeedback.trim() || null
      });
      
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600">Validating...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token || !valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-600">Invalid or expired feedback link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="pt-6 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 text-[#3477f8] mx-auto" />
            <h2 className="text-2xl font-bold">Thank you for your feedback! 🙏</h2>
            <p className="text-gray-600">
              Your input helps us improve MealGuide for everyone.
            </p>
            <p className="text-sm text-gray-500 pt-4">
              You can close this page now.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-2xl">We'd love your feedback</CardTitle>
          <CardDescription className="text-base">
            Help us understand what we can improve
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="text-base font-semibold mb-3 block">
                Why did you cancel?
              </Label>
              <div className="space-y-3">
                {CANCELLATION_REASONS.map(reason => (
                  <div key={reason.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={reason.id}
                      checked={selectedReasons.includes(reason.id)}
                      onCheckedChange={() => handleReasonToggle(reason.id)}
                    />
                    <label
                      htmlFor={reason.id}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {reason.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="feedback" className="text-base font-semibold mb-2 block">
                Additional feedback (optional)
              </Label>
              <Textarea
                id="feedback"
                placeholder="Tell us more about your experience..."
                value={additionalFeedback}
                onChange={(e) => setAdditionalFeedback(e.target.value)}
                rows={5}
                className="resize-none"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#3477f8] hover:bg-[#2563eb]"
              size="lg"
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
